/**
 * Iter-46: Landing-page SEO injector.
 *
 * Fetches server-authoritative SEO metadata for a landing page slug and
 * injects it into <head>: title, meta description, canonical, Open Graph,
 * Twitter card, JSON-LD structured data.
 *
 * Called on mount by:
 *   - LandingVertical.jsx (hardcoded verticals)
 *   - CustomLandingPage.jsx (admin-authored custom LPs)
 *
 * Idempotent — safe to call whenever the slug changes; every injected
 * element carries `data-seo="lp"` so we can clean previous injections
 * before adding new ones.
 */
import { useEffect } from "react";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

function upsertMeta(attrKey, attrValue, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attrKey}="${attrValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrKey, attrValue);
    el.setAttribute("data-seo", "lp");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "lp");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function clearInjectedJsonLd() {
  document
    .querySelectorAll('script[type="application/ld+json"][data-seo="lp"]')
    .forEach((el) => el.remove());
}

function appendJsonLd(block) {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-seo", "lp");
  script.textContent = JSON.stringify(block);
  document.head.appendChild(script);
}

/**
 * Apply an SEO payload (as returned by GET /api/public/landing-meta/{slug})
 * to the document head.
 */
export function applyLandingSeo(seo) {
  if (!seo || typeof seo !== "object") return;
  if (seo.title) document.title = seo.title;
  upsertMeta("name", "description", seo.description);
  upsertMeta("name", "keywords", seo.keywords);
  upsertMeta("name", "robots", seo.robots || "index, follow");

  // Open Graph
  upsertMeta("property", "og:title", seo.title);
  upsertMeta("property", "og:description", seo.description);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", seo.canonical);
  upsertMeta("property", "og:image", seo.og_image);
  upsertMeta("property", "og:site_name", "Free Case Review");

  // Twitter card
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", seo.title);
  upsertMeta("name", "twitter:description", seo.description);
  upsertMeta("name", "twitter:image", seo.og_image);

  upsertLink("canonical", seo.canonical);

  // JSON-LD: replace any previous injection with the fresh set.
  clearInjectedJsonLd();
  if (Array.isArray(seo.json_ld)) {
    seo.json_ld.forEach(appendJsonLd);
  }
}

/**
 * React hook — fetches SEO for the given slug and injects it on mount.
 * Skipped when `preview=true` so admin previews don't pollute the tab
 * title / og image of an internal browsing session.
 */
export function useLandingSeo({ slug, preview = false }) {
  useEffect(() => {
    if (!slug || preview) return;
    let cancelled = false;
    axios
      .get(`${API}/public/landing-meta/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (cancelled) return;
        applyLandingSeo(r.data);
      })
      .catch(() => {
        /* fall through to whatever <head> already has — do not break render */
      });
    return () => {
      cancelled = true;
    };
  }, [slug, preview]);
}
